import { Button, Stack } from '@mui/material'
import * as Yup from 'yup'
import { Formik, Form, type FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'

import { EmailInput, createEmailValidationSchema } from '@dhl/inputs'
import { type PasswordReset, useGetUserLogout, usePostUserRequestPasswordReset } from '@dhl/receiver-user-service-api'

type FormValues = {
  email: string
}

type Props = {
  setAccountEmail: (email: string) => void
}

export const RecoveryForm = ({ setAccountEmail }: Props) => {
  const { t } = useTranslation()
  const { refetch: signOut } = useGetUserLogout({
    query: {
      enabled: false,
    },
  })
  const { mutateAsync: requestPasswordReset } = usePostUserRequestPasswordReset()

  const validationSchema: Yup.Schema<FormValues> = Yup.object({
    email: createEmailValidationSchema(t),
  })

  const params = new URLSearchParams(window.location.search)

  const emailParam = params.get('email')

  const initialValues: FormValues = {
    email: emailParam || '',
  }

  const onSubmit = async (values: PasswordReset, { setSubmitting, setFieldError }: FormikHelpers<PasswordReset>) => {
    try {
      await requestPasswordReset({
        data: values,
      })
      await signOut()
      setAccountEmail(values.email)
    } catch {
      setFieldError('email', t('account.recoveryPage.errorMessage'))
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({ isSubmitting }) => (
        <Form>
          <Stack spacing={2}>
            <EmailInput name="email" variant="filled" fullWidth />

            <Button
              type="submit"
              data-test="send-email-button"
              variant="contained"
              fullWidth
              color="primary"
              disabled={isSubmitting}
            >
              <span>{t('account.recoveryPage.button')}</span>
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}
